/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import useStoreFormDraft from "../../../hooks/useStoreFormDraft";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { applyStoreDraft } from "../../../redux/store/store";
import { doc } from "firebase/firestore";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import { RefreshCw, Info, X, Clock, Activity, Trash2, Plus } from "lucide-react";
import apicache from "../../../components/clearCach/clear";
import { useTranslation } from "react-i18next";

export default function ActivationSnapchatPixel({ close }) {
  const account = useSelector((state) => state.AccountReducer);
  const dispatch = useDispatch();
  const store = useSelector((state) => state.StoreReducer);
  const products = useSelector((state) => state.ProductsReducer);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useStoreFormDraft(store);
  const dynamicSnapchatPixels = (data.dynamicSnapchatPixels || []).map((value) => ({ value }));
  const setDynamicSnapchatPixels = (pixels) => setData((previous) => ({
    ...previous,
    dynamicSnapchatPixels: pixels.map((pixel) => pixel.value || ""),
  }));
  const [pixelUpdateType, setPixelUpdateType] = useState(null);
  const [isPixelActive, setIsPixelActive] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {

    setIsPixelActive(
      Boolean(
        store?.snapchatPixel ||
          store?.snapchatPixel2 ||
          store?.snapchatPixel3 ||
          store?.snapchatPixel4 ||
          (Array.isArray(store?.dynamicSnapchatPixels) &&
            store.dynamicSnapchatPixels.some((pixel) => Boolean(pixel))),
      ),
    );
  }, [store]);

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: t("servicePageActivation.snapchatPixel.alerts.ok"),
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `
        rgba(0,0,0,0.7)
      `,
      position: "center",
    });
  };

  const clearPixelValue = (pixelNumber) => {
    switch (pixelNumber) {
      case 1:
        setData({ ...data, snapchatPixel: "" });
        break;
      case 2:
        setData({ ...data, snapchatPixel2: "" });
        break;
      case 3:
        setData({ ...data, snapchatPixel3: "" });
        break;
      case 4:
        setData({ ...data, snapchatPixel4: "" });
        break;
      default:
        if (pixelNumber >= 5) {
          const index = pixelNumber - 5;
          const newPixels = [...dynamicSnapchatPixels];
          if (index < newPixels.length) {
            newPixels[index].value = "";
            setDynamicSnapchatPixels(newPixels);
          }
        }
    }
  };

  const isFieldAllowed = (pixelNumber) => {
    const plan = account?.subscription?.plan;

    if (pixelNumber === 1) return true;

    if (pixelNumber === 2) {
      return (
        plan === "الأسطورية" || plan === "الاحترافية" || plan === "المتطورة"
      );
    }

    if (pixelNumber === 3 || pixelNumber === 4) {
      return plan === "الأسطورية" || plan === "الاحترافية";
    }

    if (pixelNumber >= 5) {
      return plan === "الأسطورية";
    }

    return false;
  };

  const getPixelValue = (pixelNumber) => {
    switch (pixelNumber) {
      case 1:
        return data.snapchatPixel || "";
      case 2:
        return data.snapchatPixel2 || "";
      case 3:
        return data.snapchatPixel3 || "";
      case 4:
        return data.snapchatPixel4 || "";
      default:
        if (pixelNumber >= 5) {
          const index = pixelNumber - 5;
          return dynamicSnapchatPixels[index]?.value || "";
        }
        return "";
    }
  };

  const saveAllPixels = async () => {
    if (!data.id) {
      await showAlert("error", "Error", "Document ID is missing");
      return;
    }

    const payload = {
      ...data,
      dynamicSnapchatPixels: dynamicSnapchatPixels
        .map((pixel) => (pixel.value || "").trim())
        .filter((value) => value !== ""),
    };

    try {
      await saveStoreDraft(doc(db, "store", data.id), store, payload);
    } catch (error) {
      Swal.fire({ icon: "error", title: t("ui.notSaved"), confirmButtonText: t("ship.ok") });
    } finally {
      setLoading(false);
    }
    dispatch(applyStoreDraft(store, payload));

    setIsPixelActive(
      Boolean(
        payload.snapchatPixel ||
          payload.snapchatPixel2 ||
          payload.snapchatPixel3 ||
          payload.snapchatPixel4 ||
          payload.dynamicSnapchatPixels?.length,
      ),
    );
  };

  const handlePixelSubmit = async (pixelNumber) => {
    if (!isFieldAllowed(pixelNumber)) return;

    setPixelUpdateType(pixelNumber);

    const pixelValue = getPixelValue(pixelNumber).trim();
    const isDeactivating = pixelValue === "";

    if (!data.id) {
      await showAlert("error", "Error", "Document ID is missing");
      setPixelUpdateType(null);
      return;
    }

    if (isDeactivating) {
      const result = await Swal.fire({
        icon: "warning",
        title: t("servicePageActivation.snapchatPixel.alerts.deactivateTitle"),
        text: t("servicePageActivation.snapchatPixel.alerts.deactivateText"),
        confirmButtonText: t("servicePageActivation.snapchatPixel.alerts.ok"),
        confirmButtonColor: "rgb(141, 93, 167)",
        showCancelButton: true,
        cancelButtonText: t("services.cancel"),
      });

      if (!result.isConfirmed) {
        setPixelUpdateType(null);
        return;
      }
    }

    apicache(account, products);
    setLoading(true);

    try {
      if (pixelNumber >= 5) {
        const index = pixelNumber - 5;
        const newPixels = [...dynamicSnapchatPixels];

        if (isDeactivating) {
          newPixels.splice(index, 1);
        }

        const payload = {
          ...data,
          dynamicSnapchatPixels: newPixels
            .map((pixel) => (pixel.value || "").trim())
            .filter((value) => value !== ""),
        };

        await saveStoreDraft(doc(db, "store", data.id), store, payload);
        dispatch(applyStoreDraft(store, payload));
        setDynamicSnapchatPixels(newPixels);
      } else {
        const pixelField =
          pixelNumber === 1
            ? "snapchatPixel"
            : pixelNumber === 2
              ? "snapchatPixel2"
              : pixelNumber === 3
                ? "snapchatPixel3"
                : "snapchatPixel4";

        const payload = {
          ...data,
          [pixelField]: pixelValue,
          dynamicSnapchatPixels: dynamicSnapchatPixels
            .map((pixel) => (pixel.value || "").trim())
            .filter((value) => value !== ""),
        };

        await saveStoreDraft(doc(db, "store", data.id), store, payload);
        dispatch(applyStoreDraft(store, payload));
        setData(payload);
      }

      const hasAnyPixel = Boolean(
        (pixelNumber === 1 ? pixelValue : data.snapchatPixel) ||
          (pixelNumber === 2 ? pixelValue : data.snapchatPixel2) ||
          (pixelNumber === 3 ? pixelValue : data.snapchatPixel3) ||
          (pixelNumber === 4 ? pixelValue : data.snapchatPixel4) ||
          dynamicSnapchatPixels.some((pixel) => Boolean((pixel.value || "").trim())),
      );

      setLoading(false);
      setIsPixelActive(hasAnyPixel);

      if (isDeactivating) {
        await showAlert(
          "success",
          t("servicePageActivation.snapchatPixel.alerts.deactivatedSuccess"),
          "",
        );
      } else {
        await showAlert(
          "success",
          t("servicePageActivation.snapchatPixel.alerts.activatedSuccess"),
          "",
        );
      }
    } catch (error) {
      setLoading(false);

      if (isDeactivating) {
        await showAlert(
          "error",
          t("servicePageActivation.snapchatPixel.alerts.deactivationErrorTitle"),
          t("servicePageActivation.snapchatPixel.alerts.errorText"),
        );
      } else {
        await showAlert(
          "error",
          t("servicePageActivation.snapchatPixel.alerts.activationErrorTitle"),
          t("servicePageActivation.snapchatPixel.alerts.errorText"),
        );
      }
    } finally {
      setPixelUpdateType(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    apicache(account, products);
    setLoading(true);

    try {
      await saveAllPixels();
      setLoading(false);
      await showAlert(
        "success",
        t("servicePageActivation.snapchatPixel.alerts.activatedSuccess"),
        "",
      );
      close();
    } catch (error) {
      setLoading(false);
      await showAlert(
        "error",
        t("servicePageActivation.snapchatPixel.alerts.activationErrorTitle"),
        t("servicePageActivation.snapchatPixel.alerts.errorText"),
      );
    }
  };

  const handleAddPixel = () => {
    if (account?.subscription?.plan !== "الأسطورية") return;
    setDynamicSnapchatPixels([...dynamicSnapchatPixels, { value: "" }]);
  };

  const handlePixelChange = (index, value) => {
    const newPixels = [...dynamicSnapchatPixels];
    newPixels[index].value = value;
    setDynamicSnapchatPixels(newPixels);
  };

  const removePixel = (index) => {
    const newPixels = [...dynamicSnapchatPixels];
    newPixels.splice(index, 1);
    setDynamicSnapchatPixels(newPixels);
  };

  return (
    <div className="min-h-[400px] h-fit p-6">
      <div className="mb-6 dark:!bg-[#181919] dark:!border-[#3c3a3d] border p-6 rounded-xl">
        <h1 className="text-xl font-bold text-[#9e68ba] mb-2 flex items-center">
          <Activity className="ml-2" size={18} />
          {t("servicePageActivation.snapchatPixel.title")}
        </h1>
        <p className="text-gray-600 dark:!text-gray-300">
          {t("servicePageActivation.snapchatPixel.description")}
        </p>
      </div>

      <div
        className={`mb-4 p-3 rounded-lg ${
          isPixelActive
            ? "bg-green-50 border border-green-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
            : "bg-gray-50 border border-gray-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
        }`}
      >
        <div className="flex gap-x-2 items-center">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${
              isPixelActive ? "bg-green-500" : "bg-gray-400 dark:!bg-gray-500"
            }`}
          ></div>
          <span
            className={`text-sm font-medium ${
              isPixelActive
                ? "text-green-700"
                : "text-gray-700 dark:!text-gray-300"
            }`}
          >
            {isPixelActive
              ? t("servicePageActivation.snapchatPixel.statusActive")
              : t("servicePageActivation.snapchatPixel.statusInactive")}
          </span>
        </div>
      </div>

      <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#9e68ba] mb-4 flex items-center">
              <Activity className="ml-2" size={18} />
              {t("servicePageActivation.snapchatPixel.settingsTitle")}
            </h3>

            <div className="mb-5">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:!text-gray-300">
                  {t("servicePageActivation.snapchatPixel.pixelIdLabel")} 1
                </label>
                <div
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isPixelActive
                      ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                      : "bg-gray-100 text-gray-800 dark:!bg-gray-700 dark:!text-gray-300"
                  }`}
                >
                  {isPixelActive
                    ? t("servicePageActivation.snapchatPixel.statusEnabled")
                    : t("servicePageActivation.snapchatPixel.statusDisabled")}
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    placeholder={t("servicePageActivation.facebookApiConversion.form.pixelIdPlaceholder")}
                    value={data.snapchatPixel || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s+/g, "");
                      setData({ ...data, snapchatPixel: value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 dark:!bg-[#141414] dark:!text-gray-200 pr-10"
                  />
                  {data.snapchatPixel && (
                    <button
                      type="button"
                      onClick={() => clearPixelValue(1)}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-gray-600"
                      title={t("servicePageActivation.snapchatPixel.clearTitle")}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handlePixelSubmit(1)}
                  disabled={loading && pixelUpdateType === 1}
                  className={`px-6 py-2.5 ${
                    data.snapchatPixel
                      ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                      : "bg-red-500 hover:bg-red-600"
                  } text-white font-medium rounded-lg transition-colors flex items-center justify-center min-w-[120px] ${
                    loading && pixelUpdateType === 1
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {loading && pixelUpdateType === 1 ? (
                    <RefreshCw className="animate-spin" size={18} />
                  ) : data.snapchatPixel ? (
                    t("servicePageActivation.snapchatPixel.activate")
                  ) : (
                    t("servicePageActivation.snapchatPixel.deactivate")
                  )}
                </button>
              </div>

              {!data.snapchatPixel && !Boolean(store?.snapchatPixel) && (
                <p className="text-amber-600 text-sm mt-1">
                  {t("servicePageActivation.snapchatPixel.pixelIdInactiveWarning")}
                </p>
              )}
            </div>

            {[2, 3, 4].map((pixelNumber) => {
              const value = getPixelValue(pixelNumber);
              const allowed = isFieldAllowed(pixelNumber);
              const isActive = Boolean(value);

              return (
                <div className="mb-5" key={pixelNumber}>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:!text-gray-300">
                      {t("servicePageActivation.snapchatPixel.pixelIdLabel")} {pixelNumber}
                    </label>
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isActive
                          ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                          : "bg-gray-100 text-gray-800 dark:!bg-gray-700 dark:!text-gray-300"
                      }`}
                    >
                      {isActive
                        ? t("servicePageActivation.snapchatPixel.statusEnabled")
                        : t("servicePageActivation.snapchatPixel.statusDisabled")}
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-grow">
                      <input
                        type="text"
                        disabled={!allowed}
                        placeholder={t("servicePageActivation.facebookApiConversion.form.pixelIdPlaceholder")}
                        value={value}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/\s+/g, "");
                          if (pixelNumber === 2) {
                            setData({ ...data, snapchatPixel2: cleaned });
                          } else if (pixelNumber === 3) {
                            setData({ ...data, snapchatPixel3: cleaned });
                          } else {
                            setData({ ...data, snapchatPixel4: cleaned });
                          }
                        }}
                        className={`w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 dark:!bg-[#141414] dark:!text-gray-200 pr-10 ${
                          !allowed
                            ? "opacity-50 cursor-not-allowed bg-gray-100 dark:!bg-gray-800"
                            : ""
                        }`}
                      />
                      {value && allowed && (
                        <button
                          type="button"
                          onClick={() => clearPixelValue(pixelNumber)}
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-gray-600"
                          title={t("servicePageActivation.snapchatPixel.clearTitle")}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      disabled={!allowed || (loading && pixelUpdateType === pixelNumber)}
                      onClick={() => handlePixelSubmit(pixelNumber)}
                      className={`px-6 py-2.5 ${
                        value ? "bg-[#9b59bf] hover:bg-[#814a9f]" : "bg-red-500 hover:bg-red-600"
                      } text-white font-medium rounded-lg transition-colors flex items-center justify-center min-w-[120px] ${
                        !allowed || (loading && pixelUpdateType === pixelNumber)
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {loading && pixelUpdateType === pixelNumber ? (
                        <RefreshCw className="animate-spin" size={18} />
                      ) : value ? (
                        t("servicePageActivation.snapchatPixel.activate")
                      ) : (
                        t("servicePageActivation.snapchatPixel.deactivate")
                      )}
                    </button>
                  </div>

                  {!allowed && (
                    <p className="text-amber-600 text-sm mt-1">
                      {t("ui.premiumAccountsOnly")}
                    </p>
                  )}
                </div>
              );
            })}

            {account?.subscription?.plan === "الأسطورية" && (
              <>
                {dynamicSnapchatPixels.map((pixel, index) => {
                  const pixelNumber = index + 5;
                  const value = pixel.value || "";

                  return (
                    <div className="mb-5" key={pixelNumber}>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700 dark:!text-gray-300">
                          {t("servicePageActivation.snapchatPixel.pixelIdLabel")} {pixelNumber}
                        </label>
                        <div
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            value
                              ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                              : "bg-gray-100 text-gray-800 dark:!bg-gray-700 dark:!text-gray-300"
                          }`}
                        >
                          {value
                            ? t("servicePageActivation.snapchatPixel.statusEnabled")
                            : t("servicePageActivation.snapchatPixel.statusDisabled")}
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row gap-3 items-center">
                        <div className="relative flex-grow">
                          <input
                            type="text"
                            placeholder={t("servicePageActivation.facebookApiConversion.form.pixelIdPlaceholder")}
                            value={value}
                            onChange={(e) =>
                              handlePixelChange(index, e.target.value.replace(/\s+/g, ""))
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] dark:!bg-[#141414] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 pr-10 dark:!text-gray-300"
                          />
                          {value && (
                            <button
                              type="button"
                              onClick={() => clearPixelValue(pixelNumber)}
                              className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-[#3c3a3d]"
                              title={t("servicePageActivation.snapchatPixel.clearTitle")}
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => removePixel(index)}
                          className="p-2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handlePixelSubmit(pixelNumber)}
                          disabled={loading && pixelUpdateType === pixelNumber}
                          className={`px-6 py-2.5 ${
                            value
                              ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                              : "bg-red-500 hover:bg-red-600"
                          } text-white font-medium rounded-lg transition-colors flex items-center justify-center min-w-[120px] ${
                            loading && pixelUpdateType === pixelNumber
                              ? "opacity-70 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          {loading && pixelUpdateType === pixelNumber ? (
                            <RefreshCw className="animate-spin" size={18} />
                          ) : value ? (
                            t("servicePageActivation.snapchatPixel.activate")
                          ) : (
                            t("servicePageActivation.snapchatPixel.deactivate")
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-4 mb-6">
                  <button
                    type="button"
                    onClick={handleAddPixel}
                    className="flex items-center justify-center px-6 py-2.5 bg-[#9b59bf] hover:bg-[#814a9f] text-white font-medium rounded-lg transition-colors gap-2"
                  >
                    <Plus size={18} />
                    <span>{t("ui.addPixel")}</span>
                  </button>
                </div>
              </>
            )}

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-3 bg-[#9b59bf] hover:bg-[#814a9f] text-white font-medium rounded-lg transition-colors flex items-center ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? (
                  <>
                    <RefreshCw className="animate-spin ml-2" size={18} />
                    {t("ui.saving")}
                  </>
                ) : (
                  <>{t("ui.savePixels")}</>
                )}
              </button>
            </div>

            <div className="mt-2 text-sm text-gray-600 dark:!text-gray-400">
              <p>{t("servicePageActivation.snapchatPixel.helpText1")}</p>
              <p>{t("servicePageActivation.snapchatPixel.helpText2")}</p>
            </div>
          </div>
        </form>
      </div>

      <div className="bg-blue-50 dark:!bg-[#181919] rounded-xl p-5 border border-blue-100 dark:!border-[#3c3a3d] mb-6">
        <h3 className="text-lg font-semibold text-blue-800 dark:!text-gray-200 mb-3 flex items-center">
          <Info className="ml-2" size={18} />
          {t("servicePageActivation.snapchatPixel.howToFind.title")}
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-700 dark:!text-gray-400 mr-2">
          <li>{t("servicePageActivation.snapchatPixel.howToFind.step1")}</li>
          <li>{t("servicePageActivation.snapchatPixel.howToFind.step2")}</li>
          <li>{t("servicePageActivation.snapchatPixel.howToFind.step3")}</li>
          <li>{t("servicePageActivation.snapchatPixel.howToFind.step4")}</li>
          <li>{t("servicePageActivation.snapchatPixel.howToFind.step5")}</li>
        </ol>
      </div>

    </div>
  );
}
